/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import {
    object, shape, array, string, bool, arrayOf, number, func,
} from 'prop-types';
import { parse } from 'qs';

import { useDispatch } from 'react-redux';
import GraphqlSearchPageContainer from './GraphqlSearchPageContainer';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

function GraphqlSearchBuilder({
    pageData,
    searchTerm,
    fetchMoreResults,
    filterOptions,
    loadMoreCount,
    pageCount,
    searchResults,
    setFilterOptions,
    track,
    productFacetsGroup,
    collectionData,
    articleData,
    variables,
    checkedFacetsData,
    prevProductCount,
    productsToLoadOnLoadMoreButton,
    productCount,
    productLoading,
}) {
    const {
        redirectUrl,
    } = pageData;
    const dispatch = useDispatch();

    if (redirectUrl) {
        // If redirect is absolute URL, change it to relative
        // Does redirect match pattern:   http or https://domain/rest-of-url  ?
        const m = redirectUrl.match(/^http(s)?:\/\/[^\/]+(\/.*?)$/i);    // eslint-disable-line no-useless-escape
        const redirectPage = m ? m[2] : redirectUrl;   // if matches pattern, redirectPage = /rest-of-url
        const isSku = /[0-9]/.test(searchTerm); // TODO: make this RE more specific towards sku
        let query = null;
        if (typeof window !== 'undefined') {
            const currentURL = (window.location?.search || '').replace('?', '');
            query = parse(currentURL);
        }

        let fromSuggestion = false;
        if (query?.searchTerm) {
            fromSuggestion = true;
        }
        dispatch(trackEvent({
            eventCategory: 'Search Redirect',
            eventAction: redirectUrl,
            eventLabel: searchTerm,
        }));
        dispatch(trackEvent({
            eventCategory: 'Search',
            eventAction: 'search_redirect',
            eventLabel: 'SEARCH INTERACTION | <<pageType>>',
            searchVariety: 'search_redirect',
            searchPhrase: searchTerm,
            eventName: 'search interaction',

            search_type: 'search_redirect',
            search_term: searchTerm,
            unique_search_term: searchTerm,
        }));

        let additionalParam = '';
        // facet filter not required for sku redirect
        if (checkedFacetsData?.length > 0 && !isSku) {
            additionalParam = 'facet=';
            checkedFacetsData.forEach((facet) => {
                additionalParam = `${additionalParam}${facet}&`;
            });
        }
        let urlOnly = redirectPage;
        if (redirectPage?.indexOf?.('?') >= 0) {
            const redirectUrlData = redirectPage.split('?');
            additionalParam = `${additionalParam}${redirectUrlData?.[1]}&`;
            urlOnly = redirectUrlData?.[0];
        }
        return (
            <Route>
                <Redirect
                    to={{
                        pathname: redirectPage[0] === '/' ? urlOnly : `/${urlOnly}`,
                        search: isSku ? `${additionalParam}redirect-for-sku=true&q=${searchTerm}&qs=${fromSuggestion}` : `${additionalParam}redirect-for-category=true&q=${searchTerm}&qs=${fromSuggestion}`,
                    }}
                />
            </Route>
        );
    }

    return (
        <GraphqlSearchPageContainer
            fetchMoreResults={fetchMoreResults}
            loadMoreCount={loadMoreCount}
            filterOptions={filterOptions}
            pageCount={pageCount}
            pageData={pageData}
            searchResults={searchResults}
            searchTerm={searchTerm}
            setFilterOptions={setFilterOptions}
            track={track}
            page={{
                type: 'search', name: 'Search', path: `/searchterm/${searchTerm}`, searchTerm,
            }}
            productFacetsGroup={productFacetsGroup}
            collectionData={collectionData}
            articleData={articleData}
            variables={variables}
            prevProductCount={prevProductCount}
            productsToLoadOnLoadMoreButton={productsToLoadOnLoadMoreButton}
            productCount={productCount}
            productLoading={productLoading}
        />
    );
}

GraphqlSearchBuilder.propTypes = {
    searchTerm: string,
    pageData: shape({
        products: array.isRequired,
        redirectUrl: string,
        showingResultsFor: string,
        originalQuery: string,
    }).isRequired,
    loadMoreCount: number,
    pageCount: number.isRequired,
    setFilterOptions: func.isRequired,
    fetchMoreResults: func.isRequired,
    track: func.isRequired,
    searchResults: shape({
        category_blocks: arrayOf(
            shape({
                image: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    mobile_image: shape({
                        url: string.isRequired,
                    }),
                }),
            }),
        ),
    }),
    filterOptions: object,
    productFacetsGroup: arrayOf(shape({
        displayOpen: bool,
        name: string,
    })),
    collectionData: shape({
        collections: arrayOf(shape({
            brand: string,
            name: string,
            description: string,
            redirectUrl: string,
            image: string,
            seo: object,
        })),
        total: number,
        pages: number,
    }),
    articleData: shape({
        blogs: arrayOf(shape({
            uid: string,
            title: string,
            excerpt: string,
            image: string,
            contentType: string,
            dateModified: string,
            datePublished: string,
            url: string,
        })),
        total: number,
        pages: number,
    }),
    variables: object,
    checkedFacetsData: arrayOf(string),
    prevProductCount: number,
    productsToLoadOnLoadMoreButton: number,
    productCount: number,
    productLoading: bool,
};

GraphqlSearchBuilder.defaultProps = {
    searchTerm: '',
    searchResults: {},
    loadMoreCount: 1,
    filterOptions: {},
    productFacetsGroup: [],
    collectionData: {},
    articleData: {},
    variables: {},
    checkedFacetsData: [],
    prevProductCount: 0,
    productsToLoadOnLoadMoreButton: null,
    productCount: 0,
    productLoading: false,
};

export default GraphqlSearchBuilder;
